import React, { useState, useRef, useEffect } from 'react';
import {
  getMe, updateMe, changePassword, uploadSignature, deleteSignature,
  getGoogleCalendarStatus, iniciarConexionGoogleCalendar, desconectarGoogleCalendar, actualizarCalendarioSeleccionado,
} from '../api';
import ModalConfirmacion from './ModalConfirmacion';

const PROFESSION_CHOICES = [
  { value: 'psychologist',    label: 'Psicología' },
  { value: 'psychiatrist',    label: 'Psiquiatría' },
  { value: 'nutritionist',    label: 'Nutrición' },
  { value: 'general_doctor',  label: 'Medicina General' },
  { value: 'pediatrician',    label: 'Pediatría' },
  { value: 'dentist',         label: 'Odontología' },
  { value: 'physiotherapist', label: 'Kinesiología' },
  { value: 'other',           label: 'Otra' },
];

function ModalGestionUsuario({ onCerrar, onUsuarioActualizado, tabInicial = 'datos', mensajeInicial = null }) {
  const [user, setUser]                 = useState(() => JSON.parse(localStorage.getItem('user') || '{}'));

  const [tab, setTab]                   = useState(tabInicial);
  const [nombre, setNombre]             = useState(user.first_name || '');
  const [apellido, setApellido]         = useState(user.last_name || '');
  const [username, setUsername]         = useState(user.username || '');
  const [email, setEmail]               = useState(user.email || '');
  const [pwActual, setPwActual]         = useState('');
  const [pwNueva, setPwNueva]           = useState('');
  const [pwConfirm, setPwConfirm]       = useState('');
  const [profRequest, setProfRequest]   = useState(user.profession_request || '');
  const [mensajeOk, setMensajeOk]       = useState('');
  const [mensajeErr, setMensajeErr]     = useState('');
  const [guardando, setGuardando]       = useState(false);
  const [firmaPreview, setFirmaPreview] = useState(user.signature_url || null);
  const [firmaTexto, setFirmaTexto]     = useState(user.signature_text || '');
  const inputFirmaRef = useRef(null);

  // Google Calendar (etapa 3a)
  const [gcalStatus, setGcalStatus]           = useState(null); // null = todavía no se cargó
  const [gcalCargando, setGcalCargando]       = useState(false);
  const [gcalErrorCarga, setGcalErrorCarga]   = useState('');
  const [gcalConectando, setGcalConectando]   = useState(false);
  const [gcalDesconectando, setGcalDesconectando] = useState(false);
  const [gcalConfirmDesconectar, setGcalConfirmDesconectar] = useState(false);
  const [gcalGuardandoCalId, setGcalGuardandoCalId] = useState(null); // id del calendario en PATCH

  // El usuario guardado en localStorage es una foto del momento del login: si
  // un admin aprobó/rechazó la profesión mientras esta sesión seguía abierta,
  // ese cambio no se reflejaba hasta volver a loguearse. Al abrir el modal
  // pedimos el estado real al backend y sincronizamos localStorage + App.js.
  useEffect(() => {
    let cancelado = false;
    getMe()
      .then((freshUser) => {
        if (cancelado) return;
        setUser(freshUser);
        setProfRequest(freshUser.profession_request || '');
        localStorage.setItem('user', JSON.stringify(freshUser));
        if (onUsuarioActualizado) onUsuarioActualizado(freshUser);
      })
      .catch(() => {
        // Si falla el refetch, seguimos mostrando los datos cacheados.
      });
    return () => { cancelado = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const limpiarMensajes = () => { setMensajeOk(''); setMensajeErr(''); };

  const mostrarOk = (txt) => { setMensajeOk(txt); setMensajeErr(''); setTimeout(() => setMensajeOk(''), 3000); };
  const mostrarErr = (txt) => { setMensajeErr(txt); setMensajeOk(''); };

  // Mensaje de vuelta del callback de OAuth de Google (ver App.js) — se
  // muestra una sola vez al abrir el modal, con el mismo banner ok/error
  // que ya usa el resto de las pestañas.
  useEffect(() => {
    if (!mensajeInicial) return;
    if (mensajeInicial.tipo === 'error') mostrarErr(mensajeInicial.texto);
    else mostrarOk(mensajeInicial.texto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Carga el estado de Google Calendar recién al abrir la pestaña (no de
  // entrada como "Mis datos") — evita el fetch cuando el profesional nunca
  // llega a mirarla.
  useEffect(() => {
    if (tab !== 'gcal') return;
    let cancelado = false;
    setGcalCargando(true);
    setGcalErrorCarga('');
    getGoogleCalendarStatus()
      .then((data) => { if (!cancelado) setGcalStatus(data); })
      .catch(() => { if (!cancelado) setGcalErrorCarga('No se pudo cargar el estado de Google Calendar.'); })
      .finally(() => { if (!cancelado) setGcalCargando(false); });
    return () => { cancelado = true; };
  }, [tab]);

  const conectarGoogleCalendar = async () => {
    limpiarMensajes();
    setGcalConectando(true);
    try {
      await iniciarConexionGoogleCalendar(); // redirige a Google — no vuelve si sale bien
    } catch (err) {
      setGcalConectando(false);
      mostrarErr(err.message);
    }
  };

  const cambiarCalendarioSeleccionado = async (calendario, nuevoValor) => {
    limpiarMensajes();
    setGcalGuardandoCalId(calendario.id);
    setGcalStatus(prev => ({
      ...prev,
      calendars: prev.calendars.map(c => c.id === calendario.id ? { ...c, selected: nuevoValor } : c),
    }));
    try {
      await actualizarCalendarioSeleccionado(calendario.id, nuevoValor);
    } catch (err) {
      // Revertir el update optimista.
      setGcalStatus(prev => ({
        ...prev,
        calendars: prev.calendars.map(c => c.id === calendario.id ? { ...c, selected: calendario.selected } : c),
      }));
      mostrarErr(err.message);
    } finally {
      setGcalGuardandoCalId(null);
    }
  };

  const confirmarDesconectarGoogle = async () => {
    limpiarMensajes();
    setGcalDesconectando(true);
    try {
      await desconectarGoogleCalendar();
      setGcalStatus({ connected: false });
      setGcalConfirmDesconectar(false);
      mostrarOk('Google Calendar desconectado.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGcalDesconectando(false);
    }
  };

  const formatearFechaHora = (iso) => new Date(iso).toLocaleString('es-AR', {
    day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });

  const ultimaSincronizacion = () => {
    if (!gcalStatus?.calendars?.length) return null;
    const fechas = gcalStatus.calendars
      .filter(c => c.selected && c.last_synced_at)
      .map(c => c.last_synced_at);
    if (!fechas.length) return null;
    return fechas.reduce((max, f) => (f > max ? f : max));
  };

  const guardarDatos = async (e) => {
    e.preventDefault();
    limpiarMensajes();
    setGuardando(true);
    try {
      const nuevoUser = await updateMe({ first_name: nombre, last_name: apellido, username, email, signature_text: firmaTexto || null });
      setUser(nuevoUser);
      localStorage.setItem('user', JSON.stringify(nuevoUser));
      if (onUsuarioActualizado) onUsuarioActualizado(nuevoUser);
      mostrarOk('Datos actualizados correctamente.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const guardarPassword = async (e) => {
    e.preventDefault();
    limpiarMensajes();
    if (pwNueva !== pwConfirm) {
      mostrarErr('Las contraseñas nuevas no coinciden.');
      return;
    }
    setGuardando(true);
    try {
      await changePassword(pwActual, pwNueva);
      setPwActual(''); setPwNueva(''); setPwConfirm('');
      mostrarOk('Contraseña cambiada correctamente.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const subirFirma = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    limpiarMensajes();
    setGuardando(true);
    try {
      const nuevoUser = await uploadSignature(file);
      setUser(nuevoUser);
      localStorage.setItem('user', JSON.stringify(nuevoUser));
      setFirmaPreview(nuevoUser.signature_url);
      if (onUsuarioActualizado) onUsuarioActualizado(nuevoUser);
      mostrarOk('Firma subida correctamente.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGuardando(false);
      e.target.value = '';
    }
  };

  const eliminarFirma = async () => {
    limpiarMensajes();
    setGuardando(true);
    try {
      const nuevoUser = await deleteSignature();
      setUser(nuevoUser);
      localStorage.setItem('user', JSON.stringify(nuevoUser));
      setFirmaPreview(null);
      if (onUsuarioActualizado) onUsuarioActualizado(nuevoUser);
      mostrarOk('Firma eliminada.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGuardando(false);
    }
  };

  const enviarSolicitudProfesion = async (e) => {
    e.preventDefault();
    limpiarMensajes();
    if (!profRequest) {
      mostrarErr('Selecciona una profesión para solicitar.');
      return;
    }
    setGuardando(true);
    try {
      const nuevoUser = await updateMe({ profession_request: profRequest });
      setUser(nuevoUser);
      localStorage.setItem('user', JSON.stringify(nuevoUser));
      if (onUsuarioActualizado) onUsuarioActualizado(nuevoUser);
      mostrarOk('Solicitud enviada. Un administrador la revisará.');
    } catch (err) {
      mostrarErr(err.message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <>
    <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && onCerrar()}>
      <div className="modal-card">
        <div className="modal-header">
          <h3>Gestión de usuario</h3>
          <button className="modal-btn-cerrar" onClick={onCerrar}>✕</button>
        </div>

        <div className="modal-body">
          {mensajeOk  && <div className="modal-mensaje-ok">{mensajeOk}</div>}
          {mensajeErr && <div className="modal-mensaje-error">{mensajeErr}</div>}

          <div className="modal-tabs">
            <button className={`modal-tab ${tab === 'datos'    ? 'modal-tab-activo' : ''}`} onClick={() => { setTab('datos');    limpiarMensajes(); }}>Mis datos</button>
            <button className={`modal-tab ${tab === 'password' ? 'modal-tab-activo' : ''}`} onClick={() => { setTab('password'); limpiarMensajes(); }}>Contraseña</button>
            <button className={`modal-tab ${tab === 'profesion'? 'modal-tab-activo' : ''}`} onClick={() => { setTab('profesion');limpiarMensajes(); }}>Mi profesión</button>
            <button className={`modal-tab ${tab === 'gcal'     ? 'modal-tab-activo' : ''}`} onClick={() => { setTab('gcal');     limpiarMensajes(); }}>Google Calendar</button>
          </div>

          {/* ── Tab: Mis datos ─────────────────────────────────── */}
          {tab === 'datos' && (
            <form onSubmit={guardarDatos}>
              <div className="modal-fila-2">
                <div className="modal-campo">
                  <label className="modal-label">Nombre</label>
                  <input className="modal-input" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre" />
                </div>
                <div className="modal-campo">
                  <label className="modal-label">Apellido</label>
                  <input className="modal-input" value={apellido} onChange={e => setApellido(e.target.value)} placeholder="Apellido" />
                </div>
              </div>
              <div className="modal-campo">
                <label className="modal-label">Nombre de usuario</label>
                <input className="modal-input" value={username} onChange={e => setUsername(e.target.value)} placeholder="Username" autoComplete="username" />
              </div>
              <div className="modal-campo">
                <label className="modal-label">Email</label>
                <input className="modal-input" type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email" />
              </div>
              {/* Firma de texto */}
              <div className="modal-campo" style={{ marginTop: 8 }}>
                <label className="modal-label">Firma de texto <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '12px' }}>(aparece en el PDF como pie de página)</span></label>
                <input
                  className="modal-input"
                  value={firmaTexto}
                  onChange={e => setFirmaTexto(e.target.value)}
                  placeholder="Ej: Dr. Juan García — M.P. 12345"
                  maxLength={200}
                />
              </div>

              {/* Firma dibujada */}
              <div className="modal-campo" style={{ marginTop: 8 }}>
                <label className="modal-label">Firma dibujada <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: '12px' }}>(imagen subida)</span></label>
                <div className="firma-seccion">
                  {firmaPreview ? (
                    <div className="firma-preview-wrapper">
                      <img src={firmaPreview} alt="Firma" className="firma-preview-img" />
                      <button type="button" className="firma-btn-eliminar" onClick={eliminarFirma} disabled={guardando}>
                        Eliminar firma
                      </button>
                    </div>
                  ) : (
                    <p className="firma-vacio">Sin firma cargada.</p>
                  )}
                  <button type="button" className="firma-btn-subir" onClick={() => inputFirmaRef.current.click()} disabled={guardando}>
                    {firmaPreview ? 'Reemplazar firma' : 'Subir firma'}
                  </button>
                  <input ref={inputFirmaRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={subirFirma} />
                </div>
              </div>

              <div className="modal-acciones">
                <button type="button" className="modal-btn-cancelar" onClick={onCerrar}>Cancelar</button>
                <button type="submit" className="modal-btn-guardar" disabled={guardando}>
                  {guardando ? 'Guardando...' : 'Guardar cambios'}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab: Contraseña ────────────────────────────────── */}
          {tab === 'password' && (
            <form onSubmit={guardarPassword}>
              <div className="modal-campo">
                <label className="modal-label">Contraseña actual</label>
                <input className="modal-input" type="password" value={pwActual} onChange={e => setPwActual(e.target.value)} autoComplete="current-password" />
              </div>
              <div className="modal-campo">
                <label className="modal-label">Nueva contraseña</label>
                <input className="modal-input" type="password" value={pwNueva} onChange={e => setPwNueva(e.target.value)} autoComplete="new-password" />
                <p className="form-ayuda-password" style={{ margin: 0 }}>
                  Mínimo 8 caracteres. No puede ser una contraseña muy común, completamente numérica, ni muy parecida a tu usuario, nombre o email.
                </p>
              </div>
              <div className="modal-campo">
                <label className="modal-label">Confirmar nueva contraseña</label>
                <input className="modal-input" type="password" value={pwConfirm} onChange={e => setPwConfirm(e.target.value)} autoComplete="new-password" />
              </div>
              <div className="modal-acciones">
                <button type="button" className="modal-btn-cancelar" onClick={onCerrar}>Cancelar</button>
                <button type="submit" className="modal-btn-guardar" disabled={guardando || !pwActual || !pwNueva || !pwConfirm}>
                  {guardando ? 'Cambiando...' : 'Cambiar contraseña'}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab: Mi profesión ──────────────────────────────── */}
          {tab === 'profesion' && (
            <form onSubmit={enviarSolicitudProfesion}>
              <div className="modal-campo">
                <label className="modal-label">Profesión actual</label>
                <div>
                  {user.profession_display
                    ? <span className="modal-profesion-actual">{user.profession_display}</span>
                    : <span className="admin-badge-sin">Sin asignar</span>
                  }
                </div>
              </div>

              {user.profession_request && (
                <div className="modal-solicitud-pendiente">
                  ⏳ Solicitud pendiente: <strong>
                    {PROFESSION_CHOICES.find(p => p.value === user.profession_request)?.label || user.profession_request}
                  </strong>. Un administrador la revisará.
                </div>
              )}

              <div className="modal-campo">
                <label className="modal-label">Solicitar cambio de profesión</label>
                <select
                  className="modal-input"
                  value={profRequest}
                  onChange={e => setProfRequest(e.target.value)}
                >
                  <option value="">Seleccionar profesión...</option>
                  {PROFESSION_CHOICES.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>

              <div className="modal-acciones">
                <button type="button" className="modal-btn-cancelar" onClick={onCerrar}>Cancelar</button>
                <button type="submit" className="modal-btn-guardar" disabled={guardando || !profRequest}>
                  {guardando ? 'Enviando...' : 'Enviar solicitud'}
                </button>
              </div>
            </form>
          )}

          {/* ── Tab: Google Calendar ───────────────────────────── */}
          {tab === 'gcal' && (
            <div className="gcal-tab">
              {gcalCargando && !gcalStatus && (
                <p className="gcal-info-texto">Cargando estado de Google Calendar...</p>
              )}

              {gcalErrorCarga && (
                <p className="gcal-info-texto">{gcalErrorCarga}</p>
              )}

              {gcalStatus && !gcalStatus.connected && (
                <div className="gcal-no-conectado">
                  <p className="gcal-descripcion">
                    SC Pacientes solo <strong>lee</strong> tus eventos de Google Calendar — nunca modifica ni
                    borra nada en tu calendario. Más detalle en nuestra{' '}
                    <a href="/privacidad" target="_blank" rel="noopener noreferrer">Política de Privacidad</a>.
                  </p>
                  <button type="button" className="modal-btn-guardar" onClick={conectarGoogleCalendar} disabled={gcalConectando}>
                    {gcalConectando ? 'Conectando...' : 'Conectar Google Calendar'}
                  </button>
                </div>
              )}

              {gcalStatus && gcalStatus.connected && (
                <div className="gcal-conectado">
                  {gcalStatus.needs_reconnect && (
                    <div className="gcal-aviso-reconectar">
                      <p>La conexión con Google se perdió. Volvé a conectar para seguir sincronizando eventos.</p>
                      <button type="button" className="modal-btn-guardar" onClick={conectarGoogleCalendar} disabled={gcalConectando}>
                        {gcalConectando ? 'Conectando...' : 'Reconectar'}
                      </button>
                    </div>
                  )}

                  <div className="gcal-info-fila">
                    <span className="modal-label">Cuenta conectada</span>
                    <span>{gcalStatus.google_account_email || '—'}</span>
                  </div>
                  <div className="gcal-info-fila">
                    <span className="modal-label">Conectado desde</span>
                    <span>{gcalStatus.connected_at ? formatearFechaHora(gcalStatus.connected_at) : '—'}</span>
                  </div>
                  <div className="gcal-info-fila">
                    <span className="modal-label">Última sincronización</span>
                    <span>{ultimaSincronizacion() ? formatearFechaHora(ultimaSincronizacion()) : 'Todavía no se sincronizó'}</span>
                  </div>
                  <p className="gcal-nota-sync">La sincronización corre automáticamente cada pocos minutos.</p>

                  <div className="gcal-calendarios">
                    <span className="modal-label">Calendarios</span>
                    {gcalStatus.calendars && gcalStatus.calendars.length > 0 ? (
                      gcalStatus.calendars.map(cal => (
                        <label key={cal.id} className="exportar-seccion-item gcal-calendario-item">
                          <input
                            type="checkbox"
                            className="exportar-check"
                            checked={!!cal.selected}
                            disabled={gcalGuardandoCalId === cal.id}
                            onChange={(e) => cambiarCalendarioSeleccionado(cal, e.target.checked)}
                          />
                          <span>
                            {cal.summary || cal.google_calendar_id}
                            {cal.is_primary && <span className="gcal-badge-principal">Principal</span>}
                          </span>
                        </label>
                      ))
                    ) : (
                      <p className="gcal-info-texto">Todavía no hay calendarios para mostrar.</p>
                    )}
                  </div>

                  <div className="modal-acciones">
                    <button
                      type="button"
                      className="gcal-btn-desconectar"
                      onClick={() => setGcalConfirmDesconectar(true)}
                      disabled={gcalDesconectando}
                    >
                      Desconectar
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
    {gcalConfirmDesconectar && (
      <ModalConfirmacion
        mensaje="¿Desconectar Google Calendar? Los turnos y pacientes que ya vinculaste a partir de eventos de Google NO se pierden — quedan intactos. Vas a dejar de sincronizar eventos nuevos hasta que te reconectes, y vas a tener que volver a elegir qué calendarios sincronizar."
        textoConfirmar="Desconectar"
        onConfirmar={confirmarDesconectarGoogle}
        onCancelar={() => setGcalConfirmDesconectar(false)}
      />
    )}
    </>
  );
}

export default ModalGestionUsuario;
